import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, Flag, CheckCircle2 } from "lucide-react";
import AdminShell, { Panel, StatusPill } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import KpiCard from "@/components/admin/KpiCard";
import DataTable from "@/components/admin/DataTable";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

export default function RiskCompliance() {
  const [alerts, setAlerts] = useState([]);
  const [kycQueue, setKycQueue] = useState([]);
  const alertsPager = useAdminPager();
  const kycPager = useAdminPager();
  async function load() {
    const { data } = await api.get("/api/admin/compliance/alerts", {
      params: { skip: alertsPager.skip, limit: alertsPager.limit },
    });
    const alerts = unwrapAdminList(data, "alerts");
    setAlerts(asAdminList(alerts.items));
    alertsPager.setTotal(alerts.total);
    const { data: k } = await api.get("/api/admin/kyc/list", {
      params: { status: "pending_review", skip: kycPager.skip, limit: kycPager.limit },
    });
    const kyc = unwrapAdminList(k);
    setKycQueue(kyc.items.map((row) => ({
      id: row.user?.id || row.id,
      email: row.user?.email || row.email,
      kyc_status: row.user?.kyc_status || row.kyc_status,
      kyc_submitted_at: row.user?.kyc_submitted_at || row.kyc_submitted_at,
      user_id: row.user?.id || row.user_id,
    })));
    kycPager.setTotal(kyc.total);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [alertsPager.page, kycPager.page]);
  async function resolve(id) {
    await api.post(`/api/admin/compliance/alerts/${id}/resolve`);
    load();
  }

  const alertRows = asAdminList(alerts);
  const high = alertRows.filter((a) => a.risk_level === "high" || a.risk_level === "critical").length;
  const flagged = alertRows.filter((a) => a.status !== "resolved").length;
  const resolvedToday = alertRows.filter((a) => a.status === "resolved").length;

  return (
    <AdminShell title="Compliance" subtitle="Transaction monitoring rules and compliance flags.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Active Alerts" value={alertsPager.total} icon={AlertTriangle} variant="yellow" />
        <KpiCard label="High / Critical" value={high} icon={AlertTriangle} variant="red" />
        <KpiCard label="KYC Pending" value={kycPager.total} icon={ShieldCheck} variant="purple" />
        <KpiCard label="Resolved Today" value={resolvedToday} icon={CheckCircle2} variant="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel>
          <div className="p-4 border-b border-line"><h3 className="text-[14px] font-semibold">Risk Alerts</h3></div>
          <DataTable
            testid="compliance-alerts"
            columns={[
              { key: "type", header: "Type", render: (a) => <span className="font-semibold">{a.type}</span> },
              { key: "user", header: "User", render: (a) => <span className="text-muted-foreground text-[12px]">{a.user_email}</span> },
              { key: "risk", header: "Risk", render: (a) => <StatusPill value={a.risk_level} /> },
              { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
              { key: "actions", header: "", align: "right", render: (a) => a.status !== "resolved" ? <button onClick={() => resolve(a.id)} className="text-up text-[11px] font-semibold hover:underline">Resolve</button> : null },
            ]}
            rows={alertRows}
            rowKey={(a) => a.id}
            empty="No active alerts."
          />
          <AdminPager
            page={alertsPager.page}
            total={alertsPager.total}
            pageSize={alertsPager.pageSize}
            onPage={alertsPager.setPage}
            testId="compliance-alerts-pager"
          />
        </Panel>

        <Panel>
          <div className="p-4 border-b border-line flex justify-between items-center">
            <h3 className="text-[14px] font-semibold">KYC Verification Queue</h3>
            <Link to="/admin/kyc" className="text-[11px] text-primary font-semibold hover:underline">View all →</Link>
          </div>
          <DataTable
            testid="kyc-queue"
            columns={[
              { key: "email", header: "User", render: (k) => <span className="font-semibold">{k.email}</span> },
              { key: "kyc_status", header: "Status", render: (k) => <StatusPill value={k.kyc_status} /> },
              { key: "created_at", header: "Submitted", render: (k) => <span className="text-muted-foreground text-[11px]">{k.kyc_submitted_at ? new Date(k.kyc_submitted_at).toLocaleDateString() : "—"}</span> },
              { key: "actions", header: "", align: "right", render: () => <Link to="/admin/kyc" className="text-primary text-[11px] hover:underline">Review</Link> },
            ]}
            rows={kycQueue}
            rowKey={(k) => k.id || k.user_id}
            empty="No KYC submissions awaiting review."
          />
          <AdminPager
            page={kycPager.page}
            total={kycPager.total}
            pageSize={kycPager.pageSize}
            onPage={kycPager.setPage}
            testId="kyc-queue-pager"
          />
        </Panel>
      </div>
    </AdminShell>
  );
}
